import { RULE_SET_ID, SOURCE_REFS, makeRuleTrace } from "./sourceAuthority.js";
const controllerCapacities = {
    "TP-C-710R-IP": { ui: 5, di: 2, ao: 3, do: 4, relay: 3 },
    "MZ-503": { ui: 7, di: 0, ao: 3, do: 4, relay: 3 }
};
export function solveControllerSelection(input) {
    const sameRoomWufhZones = input.sameRoomWufhZones ?? [];
    const secondaryZones = input.secondaryZones ?? [];
    const standaloneHeatOnlyZones = input.standaloneHeatOnlyZones ?? [];
    const traces = [
        makeRuleTrace("trace-controller-packing-start", "rule-controller-pack-710-secondary-before-mz", "Controller packing starts with one 710R per FCU, then fills 710R secondary slots before overflow.")
    ];
    const controllers = input.fcuZones.map((fcuZone) => {
        const combinedWufhZones = sameRoomWufhZones
            .filter((zone) => zone.sameRoomAsFcuId === fcuZone.id)
            .map((zone) => zone.id);
        return {
            id: `controller-710-${fcuZone.id}`,
            productPartNo: "TP-C-710R-IP",
            role: "fcu_primary",
            physicalLocation: "fcu_mounted",
            zoneSlots: [
                {
                    slot: 1,
                    role: "primary",
                    zoneId: fcuZone.id,
                    zoneKind: fcuZone.kind,
                    combinedLoadZoneIds: combinedWufhZones,
                    notes: combinedWufhZones.length > 0
                        ? "Primary FCU slot includes same-room WUFH as two-stage heating."
                        : "Primary FCU slot."
                }
            ]
        };
    });
    const uncombinedSameRoomWufhZones = sameRoomWufhZones.filter((zone) => !controllers.some((controller) => controller.zoneSlots.some((slot) => slot.combinedLoadZoneIds.includes(zone.id))));
    const pendingZones = [
        ...secondaryZones,
        ...uncombinedSameRoomWufhZones,
        ...standaloneHeatOnlyZones
    ];
    const unassignedZones = [];
    for (const zone of pendingZones) {
        const controllerWithFreeSecondary = controllers.find((controller) => controller.productPartNo === "TP-C-710R-IP" &&
            !controller.zoneSlots.some((slot) => slot.slot === 2));
        if (controllerWithFreeSecondary) {
            controllerWithFreeSecondary.zoneSlots.push({
                slot: 2,
                role: "secondary",
                zoneId: zone.id,
                zoneKind: zone.kind,
                combinedLoadZoneIds: [],
                notes: "Packed into 710R secondary slot before considering MZ overflow."
            });
        }
        else {
            unassignedZones.push(zone);
        }
    }
    if (unassignedZones.length === 1 && unassignedZones[0]?.kind === "heat_only") {
        const zone = unassignedZones.splice(0, 1)[0];
        if (!zone) {
            throw new Error("Expected one standalone heat-only overflow zone.");
        }
        controllers.push({
            id: `controller-710-${zone.id}`,
            productPartNo: "TP-C-710R-IP",
            role: "heat_only_710",
            physicalLocation: "fcu_mounted",
            zoneSlots: [
                {
                    slot: 1,
                    role: "primary",
                    zoneId: zone.id,
                    zoneKind: zone.kind,
                    combinedLoadZoneIds: [],
                    notes: "Single standalone heat-only overflow uses an additional 710R rather than MZ."
                }
            ]
        });
        traces.push(makeRuleTrace("trace-single-heat-only-extra-710", "rule-controller-single-heat-only-710", "Single standalone heat-only overflow allocated to an additional 710R."));
    }
    for (let index = 0; index < unassignedZones.length; index += 4) {
        const overflowZones = unassignedZones.slice(index, index + 4);
        if (overflowZones.length === 0) {
            continue;
        }
        controllers.push({
            id: `controller-mz-overflow-${Math.floor(index / 4) + 1}`,
            productPartNo: "MZ-503",
            role: "mz_overflow",
            physicalLocation: "central_panel",
            zoneSlots: overflowZones.map((zone, zoneIndex) => ({
                slot: zoneIndex + 1,
                role: "overflow",
                zoneId: zone.id,
                zoneKind: zone.kind,
                combinedLoadZoneIds: [],
                notes: "MZ overflow slot used only after all 710R secondary slots were exhausted."
            }))
        });
    }
    if (unassignedZones.length > 0) {
        traces.push(makeRuleTrace("trace-mz-overflow-after-710-secondaries", "rule-controller-pack-710-secondary-before-mz", "MZ overflow was considered only after no 710R secondary slots remained."));
    }
    return {
        controllers,
        unassignedZones: [],
        traces
    };
}
export function getControllerCapacity(partNo) {
    return controllerCapacities[partNo];
}
export function validateControllerZoneCapacity(controller) {
    const is710 = controller.productPartNo === "TP-C-710R-IP";
    const maxZoneSlots = is710 ? 2 : 4;
    const overCapacity = controller.zoneSlots.length > maxZoneSlots;
    return {
        id: `constraint-zone-capacity-${controller.id}`,
        ruleSetId: RULE_SET_ID,
        constraintKey: "controller.zone_capacity",
        status: overCapacity ? "fail" : "pass",
        subjectRef: controller.id,
        message: overCapacity
            ? `${controller.productPartNo} has ${controller.zoneSlots.length} zone slots, above its ${maxZoneSlots} slot limit.`
            : `${controller.productPartNo} zone slot count is within its ${maxZoneSlots} slot limit.`,
        details: {
            productPartNo: controller.productPartNo,
            maxZoneSlots,
            actualZoneSlots: controller.zoneSlots.length
        },
        sourceRefs: [SOURCE_REFS.methodologyDecisions],
        ruleTraceRefs: []
    };
}
export function validateMzAfter710Secondaries(plan) {
    const hasMz = plan.controllers.some((controller) => controller.productPartNo === "MZ-503");
    const free710SecondarySlots = plan.controllers.filter((controller) => controller.productPartNo === "TP-C-710R-IP" &&
        !controller.zoneSlots.some((slot) => slot.slot === 2));
    const violatesPackingOrder = hasMz && free710SecondarySlots.length > 0;
    return {
        id: "constraint-mz-after-710-secondaries",
        ruleSetId: RULE_SET_ID,
        constraintKey: "controller.mz_after_710_secondaries",
        status: violatesPackingOrder ? "fail" : "pass",
        subjectRef: "controller-plan",
        message: violatesPackingOrder
            ? "MZ appears while at least one 710R secondary slot is still free."
            : "MZ allocation, if present, occurs only after 710R secondary slots are filled.",
        details: {
            hasMz,
            free710SecondaryControllerIds: free710SecondarySlots.map((controller) => controller.id)
        },
        sourceRefs: [SOURCE_REFS.baselineRules],
        ruleTraceRefs: plan.traces.map((trace) => trace.id)
    };
}
export function validateControllerPlan(plan) {
    return [
        ...plan.controllers.map((controller) => validateControllerZoneCapacity(controller)),
        validateMzAfter710Secondaries(plan)
    ];
}
//# sourceMappingURL=controllerSelection.js.map